import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Icon, Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import confettiIcon from 'images/icons/confetti.png';
import hotsaleIcon from 'images/icons/hotsale.svg';
import discountGFA from 'images/brands-unique/gfa/early-discount.png';
import discountETN from 'images/brands-unique/etn/discountETN.png';
import { useTranslation } from 'react-i18next';
import { formatPercent } from 'utils/Helpers';
import {
  hasUrgencyDiscountAmount,
  isResultDiscountUIType,
  isUrgencyDiscountType,
} from 'utils/urgencyDiscount';
import WalletPrice from './WalletPrice';
import DynamicPricingDiscount, { DiscountBadge } from './DynamicPricingDiscount';

const earlyDiscountIcons = {
  etn: discountETN,
  costaline: discountETN,
  gho: discountETN,
  gfa: discountGFA,
};

/**
 * Whether a formatted percentage label states a discount the customer can actually read.
 * `formatPercent` returns whole units, so a table fare less than a full percent above the total
 * formats to `0%` — the office-price branch below floors, and this only ever sees that branch's
 * output. `Math.abs` is required, not defensive: the label carries its sign, and
 * `parseInt('-5%', 10)` is `-5`, so a bare `> 0` would suppress every badge instead of the empty ones.
 *
 * @param {string|null} percentLabel - Formatted percentage label such as `-5%`.
 * @returns {boolean} True when the label reads a non-zero percentage.
 */
const hasReadablePercent = (percentLabel) => Math.abs(parseInt(percentLabel, 10)) > 0;

/**
 * Renders the NewPriceComponent.
 *
 * @param {Object} props - The component props.
 * @param {number} props.total - The total price.
 * @param {number} props.walletTotal - The total price in wallet points.
 * @param {boolean} props.isHighligth - Indicates if the price should be highlighted.
 * @param {string} props.walletType - The type of points.
 * @param {boolean} props.extraWalletText - Indicates if there is a reward ticket.
 * @param {boolean} props.showWalletTotal - Indicates if the wallet total should be shown.
 * @param {boolean} props.isProvider - Indicates if the price is for a provider.
 * @param {boolean} props.useDiscountCurrency - Indicates if the price should use the discount color.
 * @param {node} props.secondaryPrice - The secondary price.
 * @param {boolean} props.showOfficePriceBadge - Whether the table-fare percentage badge should be
 * rendered. Governs that badge only: the urgency-discount badge below is not gated.
 * @param {boolean} props.lighterText - Indicates if the text should be lighter.
 * @param {boolean} props.showFromLabel - Indicates if the label should be shown.
 * @param {number} props.tripAvailability - Indicates the available seats for the trip.
 * @param {boolean} props.lowAvailability - Indicates low availability seats.
 * @param {boolean} props.hasHotSaleDiscount - Indicates if the trip has a Hot Sale discount.
 * @param {Object} props.providerDiscount - Provider discount information.
 * @param {string} props.discountType - Discount type from pricing.
 * @param {number} props.totalBeforeDiscount - Total price before discount.
 * @param {number} props.officePrice - Provider office (window) price when the caller's own gate
 * accepted it, otherwise null. Only the table-fare badge reads it.
 * @param {number} props.discountAvailability - Seats available at the discounted price.
 * @param {string} props.discountExpiresAt - Discount expiration timestamp.
 * @param {boolean} props.showDiscountUrgency - Indicates if discount urgency UI is enabled.
 * @param {boolean} props.providerAnniversary - Indicates if the provider anniversary discount icon should be shown.
 * @param {string} props.priceSize - Desktop size for the primary price.
 * @param {string} props.mobilePriceSize - Mobile size for the primary price.
 * @returns {JSX.Element} The rendered component.
 */
const NewPriceComponent = ({
  total,
  walletTotal,
  isHighligth,
  walletType,
  extraWalletText,
  showWalletTotal,
  isProvider,
  useDiscountCurrency,
  secondaryPrice,
  lighterText,
  showFromLabel,
  tripAvailability,
  lowAvailability,
  hasHotSaleDiscount,
  providerDiscount,
  discountType,
  totalBeforeDiscount,
  discountAvailability,
  discountExpiresAt,
  showDiscountUrgency,
  showOfficePriceBadge,
  officePrice,
  providerAnniversary,
  priceSize,
  mobilePriceSize,
}) => {
  const { t } = useTranslation('purchase');
  const { brand } = useWhitelabelEnvs();

  const defaultColor = isHighligth ? 'primary ' : 'grayStrong';
  const defaultCurrencyColor = useDiscountCurrency ? 'discount' : defaultColor;
  const defaultPriceColor = lighterText ? 'grayLight' : defaultCurrencyColor;
  const hasProviderDiscount = hasUrgencyDiscountAmount(providerDiscount);
  const hasResultDiscountUIType = isResultDiscountUIType(discountType);
  const hasUrgencyDiscountType = isUrgencyDiscountType(discountType);
  const resolvedDiscountAvailability =
    discountAvailability ?? providerDiscount?.availability ?? null;
  // The office-price route creates no DiscountPrice row, so there is no providerDiscount to derive
  // a percentage from; fall back to the office price the caller sent. The comparison is a
  // precondition on the formatPercent call below, not a second copy of the card's gate: this is a
  // shared leaf with three callers, two of which pass nothing and land on the null default.
  const hasOfficePriceDiscount = !hasProviderDiscount && Number(officePrice) > Number(total);
  let discountPercent = null;

  if (hasProviderDiscount) {
    if (providerDiscount.percent) {
      discountPercent = `-${providerDiscount.percent}%`;
    } else {
      discountPercent = `-${formatPercent(
        totalBeforeDiscount || total + providerDiscount.amount,
        total,
      )}`;
    }
  } else if (hasOfficePriceDiscount) {
    // FLOOR, not the default ROUND: this badge states how much came off a carrier's own window
    // fare, and 39.000 off 190.000 is 20,52% — rounding advertises 21%, a discount larger than
    // the one granted. Rápido Ochoa asked for the floor explicitly (28 Sep) and the purchase
    // detail derives its percentage from the same two numbers the same way, so the two screens
    // cannot disagree. Only this branch moves; a `providerDiscount` percentage is the provider's
    // own figure and keeps the rounding it has.
    discountPercent = `-${formatPercent(officePrice, total, 'FLOOR')}`;
  }

  const showDiscountUI = hasProviderDiscount && hasResultDiscountUIType && showDiscountUrgency;
  // The crossed-out table fare stays — the caller already vetted that the two amounts differ at the
  // decimals the tenant displays — but the percentage between them can still round to `0%`, and a
  // badge reading `0%` states a discount it cannot show. Suppress the badge alone.
  const renderOfficePriceBadge =
    showOfficePriceBadge && hasOfficePriceDiscount && hasReadablePercent(discountPercent);
  const discountIcon =
    (hasUrgencyDiscountType && earlyDiscountIcons[brand]) ||
    (hasHotSaleDiscount && hotsaleIcon) ||
    (providerAnniversary && confettiIcon) ||
    null;

  if (isProvider)
    return (
      <Spacing
        size="S"
        responsiveSize="XXS"
        isResponsive
        alignItems="center"
        mobileAlign="flex-end"
      >
        <Badge type="gradient" alphaBg="M" roundCorners smallPadding>
          <Text size="XS" mobileSize="XXS" color="white">
            {t('label.open_ticket_adult_price')}
          </Text>
        </Badge>
        <LocalizedCurrency
          price={total}
          color="primary"
          size={priceSize}
          mobileSize={mobilePriceSize}
          weight="bold"
          currencyStyle="currency"
        />
      </Spacing>
    );

  if (showWalletTotal)
    return (
      <WalletPrice
        walletType={walletType}
        walletTotal={walletTotal}
        extraWalletText={extraWalletText}
      />
    );

  if (showDiscountUI) {
    return (
      <DynamicPricingDiscount
        total={total}
        discountPercent={discountPercent}
        discountAvailability={resolvedDiscountAvailability}
        expiresAt={discountExpiresAt}
        priceColor={defaultCurrencyColor}
        lighterText={lighterText}
        showFromLabel={showFromLabel}
        discountIcon={discountIcon}
        secondaryPrice={secondaryPrice}
        priceSize={priceSize}
        mobilePriceSize={mobilePriceSize}
      />
    );
  }

  return (
    <div className="fade-in animated">
      <Spacing vertical alignItems="flex-end" size="XS">
        {/* center, not baseline: the discount badge is a padded block, and baseline-aligning it
            against the price leaves it visibly off. Matches DynamicPricingDiscount's price row. */}
        <Spacing alignItems="center" size="XS">
          {showFromLabel && (
            <Text size="S" italic>
              {t('general:from')}
            </Text>
          )}
          {hasHotSaleDiscount && <Icon type={hotsaleIcon} size="M" mobileSize="S" />}
          {providerAnniversary && <Icon type={confettiIcon} size="M" mobileSize="S" />}
          {renderOfficePriceBadge && <DiscountBadge percent={discountPercent} />}
          <LocalizedCurrency
            price={total}
            color={defaultPriceColor}
            weight="bold"
            size={priceSize}
            mobileSize={mobilePriceSize}
            currencyStyle="currency"
          />
        </Spacing>
        {secondaryPrice}
        {lowAvailability && (
          <Text size="S" color="discount" italic weight="semibold">
            {t('search:available_seats', { count: tripAvailability })}
          </Text>
        )}
      </Spacing>
    </div>
  );
};

NewPriceComponent.propTypes = {
  total: PropTypes.number,
  isHighligth: PropTypes.bool,
  extraWalletText: PropTypes.bool,
  walletTotal: PropTypes.number,
  walletType: PropTypes.string,
  showWalletTotal: PropTypes.string,
  isProvider: PropTypes.bool,
  useDiscountCurrency: PropTypes.bool,
  secondaryPrice: PropTypes.node,
  lighterText: PropTypes.bool,
  showFromLabel: PropTypes.bool,
  lowAvailability: PropTypes.bool,
  tripAvailability: PropTypes.number,
  hasHotSaleDiscount: PropTypes.bool,
  providerDiscount: PropTypes.shape({
    amount: PropTypes.number,
    percent: PropTypes.number,
    availability: PropTypes.number,
  }),
  discountType: PropTypes.string,
  totalBeforeDiscount: PropTypes.number,
  discountAvailability: PropTypes.number,
  discountExpiresAt: PropTypes.string,
  showDiscountUrgency: PropTypes.bool,
  showOfficePriceBadge: PropTypes.bool,
  officePrice: PropTypes.number,
  providerAnniversary: PropTypes.bool,
  priceSize: PropTypes.string,
  mobilePriceSize: PropTypes.string,
};

NewPriceComponent.defaultProps = {
  discountExpiresAt: null,
  showDiscountUrgency: false,
  showOfficePriceBadge: true,
  officePrice: null,
  providerAnniversary: false,
  priceSize: 'XL',
  mobilePriceSize: 'L',
};

export default NewPriceComponent;
